import React from 'react';
import { Stack, Group, Flex, Checkbox, TextInput, Textarea, Select, Divider } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { MapDraftItem } from '../../hooks/useCompetencyApi';

export type DraftRow = MapDraftItem & { isKept: boolean };

interface DraftRowsProps {
  rows: DraftRow[];
  onChange: (rows: DraftRow[]) => void;
  grades: { value: string; label: string }[];
  swimlanes: { value: string; label: string }[];
}

/** Строки ИИ-черновика: галочка «оставить», название, уровень, направление, описание. */
export const DraftRows: React.FC<DraftRowsProps> = ({ rows, onChange, grades, swimlanes }) => {
  const { t } = useTranslation();
  const update = (index: number, patch: Partial<DraftRow>) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  return (
    <Stack gap="sm">
      {rows.map((row, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Divider />}
          <Stack gap={6} style={{ opacity: row.isKept ? 1 : 0.5 }}>
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <Checkbox
                mt={8}
                checked={row.isKept}
                onChange={(e) => update(i, { isKept: e.currentTarget.checked })}
                aria-label={`${t('competencyMaps.draft.keep')} ${i + 1}`}
              />
              {/* На телефоне название — отдельной строкой: рядом с двумя списками оно схлопывается. */}
              <Flex
                direction={{ base: 'column', sm: 'row' }}
                gap={{ base: 6, sm: 'sm' }}
                style={{ flex: 1, minWidth: 0 }}
              >
                <TextInput
                  style={{ flex: 1 }}
                  value={row.title}
                  maxLength={100}
                  onChange={(e) => update(i, { title: e.currentTarget.value })}
                  aria-label={`${t('competencyMaps.draft.title')} ${i + 1}`}
                  fw={500}
                />
                <Group gap="sm" wrap="nowrap" grow>
                  <Select
                    miw={0}
                    w={{ sm: 140 }}
                    data={grades}
                    value={row.gradeId}
                    allowDeselect={false}
                    onChange={(v) => v && update(i, { gradeId: v })}
                    aria-label={`${t('competencyMaps.draft.grade')} ${i + 1}`}
                  />
                  <Select
                    miw={0}
                    w={{ sm: 160 }}
                    data={swimlanes}
                    value={row.swimlaneId}
                    allowDeselect={false}
                    onChange={(v) => v && update(i, { swimlaneId: v })}
                    aria-label={`${t('competencyMaps.draft.swimlane')} ${i + 1}`}
                  />
                </Group>
              </Flex>
            </Group>
            <Textarea
              ml={34}
              autosize
              minRows={1}
              maxLength={1000}
              size="xs"
              value={row.description}
              onChange={(e) => update(i, { description: e.currentTarget.value })}
              aria-label={`${t('competencyMaps.draft.description')} ${i + 1}`}
            />
          </Stack>
        </React.Fragment>
      ))}
    </Stack>
  );
};
